/**
 * overlays.tsx — menus (popover), bottom sheets, dialogs and toasts, ported from the mockup.
 *
 * Accessibility (spec §8.3): sheets and dialogs are modal, trap focus, close on Escape and
 * return focus to the opener; menus close on outside pointer-down and Escape. Toasts are
 * `role="status"` (success) / `role="alert"` (error), max 2 at a time (spec §3.4).
 */

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import { he } from '../i18n/he.ts';

export interface MenuSpec {
  kind: 'menu';
  anchor: DOMRect;
  content: ReactNode;
  wide?: boolean;
  role?: 'menu' | 'dialog';
  onClose?: () => void;
}

export interface SheetSpec {
  kind: 'sheet';
  title: string;
  content: ReactNode;
  onClose?: () => void;
}

export interface DialogAction {
  label: string;
  tone?: 'primary' | 'danger' | 'accent';
  onClick?: () => void;
}

export interface DialogSpec {
  kind: 'dialog';
  title: string;
  text?: string;
  content?: ReactNode;
  actions: DialogAction[];
  role?: 'dialog' | 'alertdialog';
  onClose?: () => void;
}

export type LayerSpec = MenuSpec | SheetSpec | DialogSpec;

function useFocusTrap(ref: { current: HTMLElement | null }, onEscape: () => void) {
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex="0"]';
    const focusables = () => Array.from(node.querySelectorAll<HTMLElement>(selector)).filter((el) => el.offsetParent !== null);
    const first = focusables()[0];
    const timer = setTimeout(() => first?.focus({ preventScroll: true }), 30);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscape();
        return;
      }
      if (event.key !== 'Tab') return;
      const list = focusables();
      if (!list.length) return;
      const index = list.indexOf(document.activeElement as HTMLElement);
      if (event.shiftKey && index <= 0) {
        event.preventDefault();
        list[list.length - 1]?.focus({ preventScroll: true });
      } else if (!event.shiftKey && index === list.length - 1) {
        event.preventDefault();
        list[0]?.focus({ preventScroll: true });
      }
    };
    node.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      node.removeEventListener('keydown', onKey);
    };
  }, [ref, onEscape]);
}

export function Menu({ spec, onClose }: { spec: MenuSpec; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const timer = setTimeout(() => document.addEventListener('pointerdown', onPointerDown, true), 0);
    document.addEventListener('keydown', onKey);
    const focusable = ref.current?.querySelector<HTMLElement>('input, button, a');
    focusable?.focus({ preventScroll: true });
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // RTL: anchor the menu to the right edge of its trigger, clamped to the viewport.
  const top = Math.min(spec.anchor.bottom + 6, window.innerHeight - 80);
  const right = Math.max(8, window.innerWidth - spec.anchor.right);
  return (
    <div
      ref={ref}
      className={`menu${spec.wide ? ' wide' : ''}`}
      role={spec.role ?? 'menu'}
      style={{ top: `${top}px`, right: `${right}px`, left: 'auto' }}
    >
      {spec.content}
    </div>
  );
}

export function Sheet({ spec, onClose }: { spec: SheetSpec; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useFocusTrap(ref, onClose);
  return (
    <>
      <div className="overlay show" onClick={onClose} />
      <div ref={ref} className="sheet show" role="dialog" aria-modal="true" aria-label={spec.title}>
        <div className="grab" aria-hidden="true" />
        <div className="sh">
          <h2>{spec.title}</h2>
          <button type="button" className="btn-icon" aria-label={he.app.close} onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className="sb">{spec.content}</div>
      </div>
    </>
  );
}

export function Dialog({ spec, onClose }: { spec: DialogSpec; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useFocusTrap(ref, onClose);
  return (
    <div className="dialog">
      <div className="overlay show" onClick={onClose} />
      <div ref={ref} className="box" role={spec.role ?? 'dialog'} aria-modal="true" aria-labelledby="dlg-title">
        <h2 id="dlg-title">{spec.title}</h2>
        {spec.text ? <p>{spec.text}</p> : null}
        {spec.content}
        <div className="acts">
          {spec.actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={`btn${action.tone ? ` ${action.tone}` : ''}`}
              onClick={() => {
                onClose();
                action.onClick?.();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export interface ToastSpec {
  id: number;
  message: string;
  error?: boolean;
  action?: { label: string; onClick: () => void };
}

export function Toasts({ items, onDismiss }: { items: ToastSpec[]; onDismiss: (id: number) => void }) {
  return (
    <div className="toasts">
      {items.map((toast) => (
        <div key={toast.id} className={`toast${toast.error ? ' error' : ''}`} role={toast.error ? 'alert' : 'status'}>
          <Icon name={toast.error ? 'alertCircle' : 'checkCircle'} className="s" />
          <span className="grow">{toast.message}</span>
          {toast.action ? (
            <button
              type="button"
              className="btn"
              onClick={() => {
                onDismiss(toast.id);
                toast.action?.onClick();
              }}
            >
              {toast.action.label}
            </button>
          ) : null}
          <button type="button" className="x" aria-label={he.app.close} onClick={() => onDismiss(toast.id)}>
            <Icon name="x" className="xs" />
          </button>
        </div>
      ))}
    </div>
  );
}
